export type TransactionType = "ABONNEMENT" | "PUB" | "EVENT" | "RECHARGE" | "SOUTIEN";

export interface Transaction {
  id: string;
  partnerId: string; // e.g. "RNI" ou "ONG Action Jeunes"
  date: string;
  type: TransactionType;
  amount: number; // positif ou négatif
  description: string;
}

export interface PartnerFinance {
  partnerId: string;
  partnerName: string;
  partnerType: "PARTI" | "ONG" | "INSTITUTION";
  balance: number;
  transactions: Transaction[];
  hasAlert: boolean; // Si l'admin a explicitement envoyé une alerte de régularisation
  isBlocked?: boolean; // Si l'admin a bloqué le compte pour impayés
}

export interface PricingConfig {
  abonnement: { PARTI: number; ONG: number; INSTITUTION: number };
  pub: { PARTI: number; ONG: number; INSTITUTION: number };
  soutienMinimum: { PARTI: number; ONG: number; INSTITUTION: number };
}

let defaultPricing: PricingConfig = {
  abonnement: { PARTI: 5000, ONG: 500, INSTITUTION: 10000 },
  pub: { PARTI: 1000, ONG: 200, INSTITUTION: 2000 },
  soutienMinimum: { PARTI: 500, ONG: 50, INSTITUTION: 1000 }
};

export const savePricing = (config: PricingConfig) => {
  localStorage.setItem('kaynin_pricing', JSON.stringify(config));
  defaultPricing = config;
};

export const getPricing = (): PricingConfig => {
  const data = localStorage.getItem('kaynin_pricing');
  if (data) {
    try {
      defaultPricing = JSON.parse(data);
    } catch (e) {}
  }
  return defaultPricing;
};

// Données initiales factices pour la plateforme Kayn'in
let financesData: PartnerFinance[] = [
  {
    partnerId: "RNI",
    partnerName: "Rassemblement National des Indépendants",
    partnerType: "PARTI",
    balance: 1500,
    hasAlert: false,
    isBlocked: false,
    transactions: [
      { id: "tx_1", partnerId: "RNI", date: "2026-03-01T10:00:00", type: "RECHARGE", amount: 2000, description: "Recharge initiale" },
      { id: "tx_2", partnerId: "RNI", date: "2026-03-05T14:30:00", type: "PUB", amount: -500, description: "Campagne de pub régionale" },
    ]
  },
  {
    partnerId: "PAM",
    partnerName: "Parti de l'Authenticité et de la Modernité",
    partnerType: "PARTI",
    balance: -200,
    hasAlert: true,
    isBlocked: false,
    transactions: [
      { id: "tx_3", partnerId: "PAM", date: "2026-02-15T09:00:00", type: "ABONNEMENT", amount: -1000, description: "Abonnement annuel 2026" },
      { id: "tx_4", partnerId: "PAM", date: "2026-02-16T11:00:00", type: "RECHARGE", amount: 800, description: "Paiement partiel" },
    ]
  },
  {
    partnerId: "PI",
    partnerName: "Parti de l'Istiqlal",
    partnerType: "PARTI",
    balance: 0,
    hasAlert: false,
    isBlocked: false,
    transactions: [
      { id: "tx_5", partnerId: "PI", date: "2026-01-10T10:00:00", type: "ABONNEMENT", amount: -500, description: "Abonnement Semestriel" },
      { id: "tx_6", partnerId: "PI", date: "2026-01-12T10:00:00", type: "RECHARGE", amount: 500, description: "Paiement" },
    ]
  },
  {
    partnerId: "Test ONG",
    partnerName: "Association Nationale des Jeunes",
    partnerType: "ONG",
    balance: -150,
    hasAlert: false,
    isBlocked: true,
    transactions: [
      { id: "tx_7", partnerId: "Test ONG", date: "2026-04-01T10:00:00", type: "EVENT", amount: -150, description: "Publication d'événement Caritatif" },
    ]
  }
];

// Sauvegarde dans le localStorage (simulation base de données)
export const saveFinances = () => {
  localStorage.setItem('kaynin_finances', JSON.stringify(financesData));
};

export const loadFinances = () => {
  const data = localStorage.getItem('kaynin_finances');
  if (data) {
    try {
      financesData = JSON.parse(data);
    } catch (e) {
      console.error("Finances parsing error");
    }
  }
  return financesData;
};

// Fonctions d'accès et de modification
export const getAllFinances = (): PartnerFinance[] => {
  return loadFinances();
};

export const getPartnerFinance = (partnerId: string): PartnerFinance => {
  loadFinances();
  let finance = financesData.find(f => f.partnerId === partnerId);
  if (!finance) {
    finance = {
      partnerId,
      partnerName: partnerId,
      partnerType: "PARTI", // By default for fallback
      balance: 0,
      hasAlert: false,
      isBlocked: false,
      transactions: []
    };
    financesData.push(finance);
    saveFinances();
  }
  return finance;
};

export const addTransaction = (partnerId: string, type: TransactionType, amount: number, description: string) => {
  const finance = getPartnerFinance(partnerId);
  finance.transactions.push({
    id: `tx_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    partnerId,
    date: new Date().toISOString(),
    type,
    amount,
    description
  });
  finance.balance += amount;
  
  // Si le solde redevient positif, on annule l'alerte
  if (finance.balance > 0) {
    finance.hasAlert = false;
  }
  
  saveFinances();
  return finance;
};

export const alertPartner = (partnerId: string) => {
  const finance = getPartnerFinance(partnerId);
  finance.hasAlert = true;
  saveFinances();
  return finance;
};

export const removeAlert = (partnerId: string) => {
  const finance = getPartnerFinance(partnerId);
  finance.hasAlert = false;
  saveFinances();
  return finance;
};

export const blockPartner = (partnerId: string) => {
  const finance = getPartnerFinance(partnerId);
  finance.isBlocked = true;
  saveFinances();
  return finance;
};

export const unblockPartner = (partnerId: string) => {
  const finance = getPartnerFinance(partnerId);
  finance.isBlocked = false;
  saveFinances();
  return finance;
};
